import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import useAuthStore from './store/authStore';
import api from './api/axios';

// Pages
import LandingPage      from './pages/LandingPage';
import LoginPage        from './pages/LoginPage';
import RegisterPage     from './pages/RegisterPage';
import DashboardPage    from './pages/DashboardPage';
import ProfileSetupPage from './pages/ProfileSetupPage';
import SearchPage       from './pages/SearchPage';
import ProfileViewPage  from './pages/ProfileViewPage';
import InterestsPage    from './pages/InterestsPage';
import ShortlistPage    from './pages/ShortlistPage';
import ChatPage         from './pages/ChatPage';
import SubscriptionPage from './pages/SubscriptionPage';
import MyProfilePage    from './pages/MyProfilePage';
import NotificationsPage from './pages/NotificationsPage';

const PrivateRoute = ({ children }) => {
  const { token } = useAuthStore();
  return token ? children : <Navigate to="/login" replace />;
};

const PublicRoute = ({ children }) => {
  const { token } = useAuthStore();
  return !token ? children : <Navigate to="/dashboard" replace />;
};

// Pings the backend every ~60s while the tab is open and focused, so the
// server knows the user is actually looking at the site right now (not just
// "logged in at some point"). This is what lets new-message emails skip
// firing when the recipient is already online to see the in-app toast.
function usePresenceHeartbeat() {
  const token = useAuthStore((s) => s.token);
  useEffect(() => {
    if (!token) return;
    const ping = () => { if (!document.hidden) api.post('/presence/ping').catch(() => {}); };
    ping();
    const interval = setInterval(ping, 60000);
    document.addEventListener('visibilitychange', ping);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', ping);
    };
  }, [token]);
}

export default function App() {
  usePresenceHeartbeat();
  return (
    <BrowserRouter>
      <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
      <Routes>
        {/* Public */}
        <Route path="/"         element={<LandingPage />} />
        <Route path="/login"    element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />

        {/* Protected */}
        <Route path="/dashboard"       element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
        <Route path="/profile/setup"   element={<PrivateRoute><ProfileSetupPage /></PrivateRoute>} />
        <Route path="/profile/me"      element={<PrivateRoute><MyProfilePage /></PrivateRoute>} />
        <Route path="/profile/:id"     element={<PrivateRoute><ProfileViewPage /></PrivateRoute>} />
        <Route path="/search"          element={<PrivateRoute><SearchPage /></PrivateRoute>} />
        <Route path="/interests"       element={<PrivateRoute><InterestsPage /></PrivateRoute>} />
        <Route path="/shortlist"       element={<PrivateRoute><ShortlistPage /></PrivateRoute>} />
        <Route path="/chat"            element={<PrivateRoute><ChatPage /></PrivateRoute>} />
        <Route path="/chat/:id"        element={<PrivateRoute><ChatPage /></PrivateRoute>} />
        <Route path="/subscription"    element={<PrivateRoute><SubscriptionPage /></PrivateRoute>} />
        <Route path="/notifications"   element={<PrivateRoute><NotificationsPage /></PrivateRoute>} />

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
  );
}
